import { io } from 'socket.io-client';
import { API_BASE_URL } from '../config';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.eventListeners = new Map();
    this.connectionAttempted = false; // Prevent multiple connection attempts
  }

  connect() {
    
    // Check if socket connections are disabled via environment variable
    if (import.meta.env.VITE_DISABLE_SOCKET === 'true') {
      return null;
    }

    if (this.socket && this.isConnected) {
      return;
    }

    if (this.connectionAttempted && this.socket) {
      return;
    }

    this.connectionAttempted = true;
    
    // Extract the base URL without the port for socket connection
    const serverUrl = API_BASE_URL.replace(/:\d+$/, ''); // Remove port if present
    const socketUrl = serverUrl.includes('localhost') ? 'http://localhost:3000' : serverUrl;
    
    this.socket = io(socketUrl, {
      transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
      timeout: 10000,
      forceNew: true,
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      maxReconnectionAttempts: 5
    });

    this.socket.on('connect', () => {
      this.isConnected = true;
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      
      // Don't destroy socket on disconnect - let it reconnect
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't reconnect
      } else {
        // Client-side disconnect or network issue, will auto-reconnect
      }
    });

    this.socket.on('connect_error', (error) => {
      console.warn('[SocketService] Connection error:', error.message);
      this.isConnected = false;
      // Don't destroy socket on error - let it retry
    });

    this.socket.on('reconnect', (attemptNumber) => {
      this.isConnected = true;
    });

    this.socket.on('reconnect_error', (error) => {
      console.warn('[SocketService] Reconnection error:', error.message);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[SocketService] Failed to reconnect after all attempts');
      this.isConnected = false;
    });

    // Listen for S3 events
    this.socket.on('s3:new-file', (payload) => {
      this.handleS3Event(payload);
    });

    // Try to connect, but don't fail if Socket.IO isn't available
    this.socket.connect();

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.connectionAttempted = false;
    }
  }

  // Force a reconnection attempt
  reconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket.connect();
    } else {
      this.connect();
    }
  }

  // Register event listener for S3 events
  onS3Event(callback) {
    const id = Date.now() + Math.random();
    this.eventListeners.set(id, callback);
    
    return () => {
      this.eventListeners.delete(id);
    };
  }

  // Handle incoming S3 events and notify all registered listeners
  handleS3Event(payload) {

    // Notify all registered listeners
    this.eventListeners.forEach((callback, id) => {
      try {
        callback(payload);
      } catch (error) {
        console.error('[SocketService] Error in event listener:', error);
      }
    });
  }

  // Check if socket is connected
  getConnectionStatus() {
    return {
      connected: this.isConnected,
      socketId: this.socket?.id || null
    };
  }

  // Check if socket service is available (not in error state)
  isAvailable() {
    return this.socket !== null && !this.socket.disconnected;
  }

}

// Create and export a singleton instance
const socketService = new SocketService();
export default socketService;
